import { RunsQuery as LogsQuery } from '@rossum/api-client/hooks';
import {
  IconCaretDownFilled,
  IconCaretUpFilled,
  IconX,
} from '@rossum/ui/icons/tabler';
import {
  Button,
  FormControl,
  IconButton,
  Popover,
  Stack,
  SvgIcon,
  TextField,
} from '@rossum/ui/material';
import { MouseEvent, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  convertFromUTC,
  convertToUTC,
  formatDateHoursSecondsToQuery,
  formatDateTimeWithSecondsPickerValue,
} from '../../../../components/Datepicker/helpers';
import {
  activeLabelStyle,
  activeOutlineStyle,
} from '../../../../components/UI/SingleQueueSelect/styles';
import { DateTimeRangePicker } from '../../../document-list-base/components/DateTimeRangePicker';
import { SelectedDates } from '../../../document-list-base/mql/dateUtils';

type Props = {
  filters: LogsQuery;
  setFilters: (filters: LogsQuery) => void;
  width: number;
};

const DatepickerFilters = ({ filters, setFilters, width }: Props) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const beginDate = filters.timestampAfter
    ? convertFromUTC(new Date(filters.timestampAfter))
    : null;
  const endDate = filters.timestampBefore
    ? convertFromUTC(new Date(filters.timestampBefore))
    : null;
  const [selectedDates, setSelectedDates] = useState<SelectedDates>([
    beginDate,
    endDate,
  ]);
  const dateFilterActive = !!(beginDate && endDate);

  const handleSubmit = () => {
    const [from, to] = selectedDates;

    if (!from || !to) return;

    setFilters({
      ...filters,
      timestampAfter: formatDateHoursSecondsToQuery(convertToUTC(from)),
      timestampBefore: formatDateHoursSecondsToQuery(convertToUTC(to)),
    });

    handleClose();
  };

  const handleClearDates = useCallback(() => {
    setFilters({
      ...filters,
      timestampBefore: undefined,
      timestampAfter: undefined,
    });
  }, [filters, setFilters]);

  const visible = !!anchorEl;
  const currentValue = dateFilterActive
    ? `${formatDateTimeWithSecondsPickerValue(beginDate, endDate, intl.locale)}`
    : visible
      ? ' '
      : '';

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpen = (e: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(e.currentTarget);
    setSelectedDates([beginDate, endDate]);
  };
  return (
    <Stack>
      <FormControl variant="outlined" sx={{ width }}>
        <TextField
          size="small"
          autoComplete="off"
          data-cy="extension-log-date-input"
          label={intl.formatMessage({
            id: 'containers.settings.extensions.logs.filters.extension.dateRange',
          })}
          onClick={handleOpen}
          value={currentValue}
          slotProps={{
            inputLabel: {
              sx: visible || dateFilterActive ? activeLabelStyle : {},
              shrink: visible || dateFilterActive,
            },
            input: {
              // to align with the other log filter components we need 14px right padding here.
              sx: {
                pr: 0.875,
              },
              readOnly: true,
              endAdornment: (
                <Stack direction="row" alignItems="center">
                  {dateFilterActive ? (
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        handleClearDates();
                      }}
                    >
                      <SvgIcon fontSize="small">
                        <IconX />
                      </SvgIcon>
                    </IconButton>
                  ) : null}
                  <SvgIcon
                    sx={{
                      fontSize: t => t.typography.fontSize,
                      color: 'text.secondary',
                    }}
                  >
                    {visible ? <IconCaretUpFilled /> : <IconCaretDownFilled />}
                  </SvgIcon>
                </Stack>
              ),
            },
          }}
          sx={visible ? activeOutlineStyle : {}}
        />
        <Popover
          open={visible}
          onClose={handleClose}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          onTransitionExited={() => setSelectedDates([null, null])}
        >
          <Stack sx={{ py: 2, px: 1, gap: 2 }}>
            <DateTimeRangePicker
              selectedDates={selectedDates}
              setSelectedDates={setSelectedDates}
              operatorValue="is"
            />
            <Stack direction="row" sx={{ justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClose}
              >
                {intl.formatMessage({
                  id: 'containers.settings.extensions.logs.dateFilter.cancelButton',
                })}
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!selectedDates[0] || !selectedDates[1]}
              >
                {intl.formatMessage({
                  id: 'containers.settings.extensions.logs.dateFilter.applyButton',
                })}
              </Button>
            </Stack>
          </Stack>
        </Popover>
      </FormControl>
    </Stack>
  );
};

export { DatepickerFilters };
